import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as settingService from '../../services/settingService';

export const fetchSettings = createAsyncThunk(
  'settings/fetchSettings',
  async (_, { rejectWithValue }) => {
    try {
      const res = await settingService.getSettings();
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch settings');
    }
  }
);

export const updateSetting = createAsyncThunk(
  'settings/updateSetting',
  async ({ key, value }, { rejectWithValue }) => {
    try {
      const res = await settingService.updateSetting(key, value);
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to update setting');
    }
  }
);

export const fetchShopInfo = createAsyncThunk(
  'settings/fetchShopInfo',
  async (_, { rejectWithValue }) => {
    try {
      const res = await settingService.getShopInfo();
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch shop info');
    }
  }
);

export const updateShop = createAsyncThunk(
  'settings/updateShop',
  async (shopData, { rejectWithValue }) => {
    try {
      const res = await settingService.updateShop(shopData);
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to update shop');
    }
  }
);

export const updateFinancial = createAsyncThunk(
  'settings/updateFinancial',
  async (financialData, { rejectWithValue }) => {
    try {
      const res = await settingService.updateFinancial(financialData);
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to update financial settings');
    }
  }
);

export const updateReceipt = createAsyncThunk(
  'settings/updateReceipt',
  async (receiptData, { rejectWithValue }) => {
    try {
      const res = await settingService.updateReceipt(receiptData);
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to update receipt settings');
    }
  }
);

export const updateNotifications = createAsyncThunk(
  'settings/updateNotifications',
  async (notifData, { rejectWithValue }) => {
    try {
      const res = await settingService.updateNotifications(notifData);
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to update notification settings');
    }
  }
);

export const updateAppearance = createAsyncThunk(
  'settings/updateAppearance',
  async (appearanceData, { rejectWithValue }) => {
    try {
      const res = await settingService.updateAppearance(appearanceData);
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to update appearance settings');
    }
  }
);

export const uploadLogo = createAsyncThunk(
  'settings/uploadLogo',
  async (formData, { rejectWithValue }) => {
    try {
      const res = await settingService.uploadLogo(formData);
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to upload logo');
    }
  }
);

export const fetchSystemInfo = createAsyncThunk(
  'settings/fetchSystemInfo',
  async (_, { rejectWithValue }) => {
    try {
      const res = await settingService.getSystemInfo();
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch system info');
    }
  }
);

const initialState = {
  settings: null,
  shopInfo: null,
  systemInfo: null,
  loading: false,
  error: null,
};

const settingSlice = createSlice({
  name: 'settings',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSettings.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSettings.fulfilled, (state, action) => {
        state.loading = false;
        state.settings = action.payload.settings || action.payload;
      })
      .addCase(fetchSettings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updateSetting.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateSetting.fulfilled, (state, action) => {
        state.loading = false;
        state.settings = { ...state.settings, ...(action.payload.settings || action.payload) };
      })
      .addCase(updateSetting.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchShopInfo.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchShopInfo.fulfilled, (state, action) => {
        state.loading = false;
        state.shopInfo = action.payload.shop || action.payload;
      })
      .addCase(fetchShopInfo.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updateShop.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateShop.fulfilled, (state, action) => {
        state.loading = false;
        state.shopInfo = action.payload.shop || action.payload;
      })
      .addCase(updateShop.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updateFinancial.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateFinancial.fulfilled, (state, action) => {
        state.loading = false;
        state.settings = { ...state.settings, ...(action.payload.settings || action.payload) };
      })
      .addCase(updateFinancial.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updateReceipt.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateReceipt.fulfilled, (state, action) => {
        state.loading = false;
        state.settings = { ...state.settings, ...(action.payload.settings || action.payload) };
      })
      .addCase(updateReceipt.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updateNotifications.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateNotifications.fulfilled, (state, action) => {
        state.loading = false;
        state.settings = { ...state.settings, ...(action.payload.settings || action.payload) };
      })
      .addCase(updateNotifications.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updateAppearance.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateAppearance.fulfilled, (state, action) => {
        state.loading = false;
        state.settings = { ...state.settings, ...(action.payload.settings || action.payload) };
      })
      .addCase(updateAppearance.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(uploadLogo.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(uploadLogo.fulfilled, (state, action) => {
        state.loading = false;
        state.shopInfo = { ...state.shopInfo, ...(action.payload.shop || action.payload) };
      })
      .addCase(uploadLogo.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchSystemInfo.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSystemInfo.fulfilled, (state, action) => {
        state.loading = false;
        state.systemInfo = action.payload.system || action.payload;
      })
      .addCase(fetchSystemInfo.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearError } = settingSlice.actions;
export default settingSlice.reducer;
